import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchWithLanguage, getCsrfToken } from '../utils/api';

// Компонент формы входа
export const LoginForm = ({ onSuccess, onToggleForm }) => {
  const { t } = useLanguage();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await fetchWithLanguage('/api/auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({ phone, password }),
      });

      if (data.error) {
        throw new Error(data.error || t('loginError'));
      }

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form login-form">
      <style jsx="true">{`
        .full-width-input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px;
          margin-bottom: 10px;
        }
      `}</style>
      <h2>{t('login')}</h2>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="phone">{t('phone')}:</label>
          <input 
            type="tel" 
            id="phone" 
            value={phone} 
            onChange={(e) => setPhone(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">{t('password')}:</label>
          <input 
            type="password" 
            id="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? t('loading') : t('login')}
        </button>
      </form>
      <div className="form-toggle">
        <p>{t('noAccount')} <button type="button" onClick={onToggleForm}>{t('register')}</button></p>
      </div>
    </div>
  );
};

// Компонент формы регистрации
export const RegisterForm = ({ onSuccess, onToggleForm }) => {
  const { t, language } = useLanguage();
  
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError(t('passwordsNotMatch'));
      return;
    }
    
    if (!termsAgreed) {
      setError(t('termsAgreementRequired'));
      return;
    }
    
    setLoading(true);

    try {
      const data = await fetchWithLanguage('/api/auth/register/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({ 
          first_name: firstName,
          phone,
          password, 
          password_confirm: confirmPassword 
        }),
      });

      if (data.error) {
        throw new Error(data.error || t('registrationError'));
      }

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form register-form">
      <style jsx="true">{`
        .full-width-input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px;
          margin-bottom: 10px;
        }
      `}</style>
      <h2>{t('register')}</h2>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="first-name">{t('firstName')}:</label>
          <input 
            type="text" 
            id="first-name" 
            value={firstName} 
            onChange={(e) => setFirstName(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">{t('phone')}:</label>
          <input 
            type="tel" 
            id="phone" 
            value={phone} 
            onChange={(e) => setPhone(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="reg-password">{t('password')}:</label>
          <input 
            type="password" 
            id="reg-password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirm-password">{t('confirmPassword')}:</label>
          <input 
            type="password" 
            id="confirm-password" 
            value={confirmPassword} 
            onChange={(e) => setConfirmPassword(e.target.value)} 
            required
            className="full-width-input"
          />
        </div>
        <div className="terms-checkbox" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          <input
            type="checkbox"
            id="terms-agreement"
            checked={termsAgreed}
            onChange={(e) => setTermsAgreed(e.target.checked)}
            required
            style={{ marginRight: '8px' }}
          />
          <label htmlFor="terms-agreement">
            {t('termsAgreement')} <a href={`/${language}/terms/`} target="_blank" rel="noopener noreferrer">{t('termsOfService')}</a>
          </label>
        </div>
        <button type="submit" className="auth-button" disabled={loading || !termsAgreed}>
          {loading ? t('loading') : t('register')}
        </button>
      </form>
      <div className="form-toggle">
        <p>{t('haveAccount')} <button type="button" onClick={onToggleForm}>{t('login')}</button></p>
      </div>
    </div>
  );
};

// Компонент модального окна авторизации
export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [isLoginForm, setIsLoginForm] = useState(true);

  if (!isOpen) return null;

  const handleSuccess = (data) => {
    if (onSuccess) {
      onSuccess(data);
    }
    onClose();
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>&times;</button>
        {isLoginForm ? (
          <LoginForm 
            onSuccess={handleSuccess} 
            onToggleForm={() => setIsLoginForm(false)} 
          />
        ) : (
          <RegisterForm 
            onSuccess={handleSuccess} 
            onToggleForm={() => setIsLoginForm(true)} 
          />
        )}
      </div>
    </div>
  );
};

// Компонент для управления аутентификацией
export const AuthControls = ({ user, onLogin, onLogout }) => {
  const { t } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleLogout = async () => {
    try {
      await fetchWithLanguage('/api/auth/logout/', {
        method: 'POST',
        headers: {
          'X-CSRFToken': getCsrfToken(),
        }
      });
      
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="auth-controls">
      {user ? (
        <div className="user-info">
          <span className="user-email">{user.phone || user.email}</span>
          <button className="logout-btn" onClick={handleLogout}>
            {t('logout')}
          </button>
        </div>
      ) : (
        <button className="login-btn" onClick={() => setShowAuthModal(true)}>
          {t('login')} / {t('register')}
        </button>
      )}
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={onLogin}
      />
    </div>
  );
};

